import { useMutation } from "@tanstack/react-query";
import _ from "lodash";

import { QuoteCost } from "@models/Quote";
import client, { useMutateError } from "@services/api";
import asyncPool from "@utility/asyncPool";

import { QuoteFormType } from "../SupplierQuote/QuoteForm";

type SaveQuoteCostsMutationPayload = {
  quoteId: string;
  formCosts: QuoteFormType["costs"];
  savedCosts: QuoteCost[];
};

function useSaveQuoteCostsMutation() {
  const setMutateError = useMutateError();

  return useMutation({
    mutationFn: async ({
      quoteId,
      formCosts,
      savedCosts,
    }: SaveQuoteCostsMutationPayload) => {
      const savedCostsById = _(savedCosts)
        .map((c) => _.omit(c, "quote"))
        .keyBy("id")
        .value();

      const costsToDelete = savedCosts.filter(
        (cost) => !formCosts.find((c) => c.id === cost.id)
      );
      const costsToCreate = formCosts.filter((cost) => !cost.id);
      const costsToUpdate = formCosts.filter(
        (cost) => cost.id && !_.isEqual(cost, savedCostsById[cost.id])
      );

      const actions = [
        ...costsToDelete.map((c) => ["delete", c]),
        ...costsToCreate.map((c) => ["post", { ...c, quoteId }]),
        ...costsToUpdate.map((c) => ["patch", c]),
      ] as [string, QuoteCost][];

      const res = await asyncPool(5, actions, async ([method, body]) => {
        switch (method) {
          case "post":
            await client.post("costs", body);
            break;
          case "delete":
            await client.delete(`costs/${body.id}`);
            break;
          case "patch":
            await client.update(`costs/${body.id}`, body);
            break;
        }
      });

      if (res.errors) throw res.errors[0];
      return res.results;
    },

    onError: (error) => {
      setMutateError(error as Error, "Save quote costs");
    },
  });
}

export default useSaveQuoteCostsMutation;
